<template>
  <div class="workflow-service-data">
    <div v-if="!workflows || workflows.length === 0" class="text-muted p-3">No workflows to display.</div>

    <div v-else>
      <div v-for="wf in workflows" :key="key_for(wf)" class="card shadow-sm border rounded-3 mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="service-icon me-3">
              <i class="fas fa-project-diagram text-primary" aria-hidden="true"></i>
            </div>
            <div class="flex-grow-1 me-3">
              <label class="fs-6 fw-bold mb-0">{{ wf.name }}</label>
              <p class="text-xs mb-0 text-muted">{{ wf.description }}</p>
            </div>

            <div>
              <button class="btn btn-bg-secondary text-xs btn-sm rounded-3" @click="toggle(wf)"
                :aria-expanded="is_open(wf)">
                {{ is_open(wf) ? 'Collapse' : 'Expand' }}
              </button>
            </div>
          </div>

          <div v-if="is_open(wf)" class="mt-3 workflow-body">
            <div class="row g-3">
              <div class="col-12">
                <div class="small text-muted mb-2">Applications</div>
                <div v-if="Array.isArray(wf.applications) && wf.applications.length">
                  <div v-for="app in wf.applications" :key="app.id" class="card border rounded-3 mb-2">
                    <div class="card-body">
                      <div class="d-flex align-items-center mb-2">
                        <div class="me-auto fw-semibold">
                          Application created: {{ format_date_time(app.date_created) }}
                        </div>
                        <button class="btn btn-sm btn-outline-secondary" @click="toggle_app(app.id)"
                          :aria-expanded="is_app_open(app.id)">
                          {{ is_app_open(app.id) ? 'Collapse' : 'Expand' }}
                        </button>
                      </div>

                      <div v-if="is_app_open(app.id)">
                        <div v-if="app_state(app.id).loading" class="text-muted text-xs">Loading…</div>
                        <div v-else-if="app_state(app.id).error" class="alert alert-danger py-2 px-3 text-xs">{{
                          app_state(app.id).error }}</div>
                        <template v-else>
                          <div class="d-flex justify-content-between align-items-center mb-2" v-if="has_risk(app)">
                            <button class="btn btn-primary btn-sm rounded-pill" @click="toggle_risk(app)">
                              <i class="fa fa-chart-bar me-1"></i>
                              {{ is_risk_open(app) ? 'Hide Risk Assessment' : 'Show Risk Assessment' }}
                            </button>
                          </div>

                          <RiskMatrix v-if="has_risk(app) && is_risk_open(app)" :service_id="service?.id"
                            :service_name="service?.service_name || service?.name || 'Service'"
                            :user_id="app.customer_id" :application_id="app.id"
                            :service_type="get_risk_service_type()"
                            :entity_name="entity_name" />

                          <div v-if="app_state(app.id).pdf_url" class="mb-3">
                            <div class="ratio ratio-16x9 border rounded">
                              <iframe :src="app_state(app.id).pdf_url" title="Service PDF" frameborder="0"></iframe>
                            </div>
                          </div>

                          <ServiceData :service="service" :response="app_state(app.id).response"
                            :response_doc="app_state(app.id).response_doc"
                            :customer_image="app_state(app.id).customer_image_base_64" />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-xs text-muted">No applications in this workflow.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceData from '@/layouts/data_hub/components/services/service/ServiceData.vue';
import { defineAsyncComponent } from 'vue';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner.vue';
import { API_BASE_URL } from '@/config';
import { parseServiceFetchPayload } from '@/layouts/data_hub/components/services/service/serviceFetch';
import { format_date_time } from '@/utils/date_formatter';
export default {
  name: 'WorkflowServiceData',
  components: {
    ServiceData,
    RiskMatrix: defineAsyncComponent({
      loader: () => import('@/components/RiskMatrix/RiskMatrix.vue'),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
  },
  props: {
    service: {
      type: Object,
      required: false,
    },
    entity: {
      type: Object,
      required: false,
      default: null,
    },
    entityType: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      open_key: null,
      app_data: {},
      open_apps: {},
      open_risk: {},
      app_severity: {},
    };
  },
  computed: {
    workflows() {
      const items = Array.isArray(this.service?.workflows) ? this.service.workflows : [];
      return items.map(w => ({
        id: w.workflow_id ?? w.id ?? null,
        name: w.workflow_name ?? w.name ?? w.title ?? `Workflow ${w.workflow_id ?? ''}`,
        description: w.workflow_description ?? w.description ?? '',
        applications: Array.isArray(w.applications)
          ? w.applications.map(a => ({
            id: a.id ?? a.application_id ?? a.app_id ?? a,
            customer_id: a.customer_id ?? a.user_id ?? a.customer ?? null,
            date_created: a.date_created ?? a.created_at ?? a.created ?? null,
            _raw: a,
          }))
          : (Array.isArray(w.application_ids) ? w.application_ids.map(id => ({ id, date_created: null })) : []),
        _raw: w,
      }));
    },
    entity_name() {
      const isCompany = this.entityType === 'company' || this.entityType === 'enterprise';
      if (isCompany && this.entity?.name) {
        return this.entity.name;
      }
      const isIndividual = this.entityType === 'individual';
      if (isIndividual && (this.entity?.first_name || this.entity?.last_name)) {
        const firstName = this.entity.first_name || '';
        const lastName = this.entity.last_name || '';
        return `${firstName} ${lastName}`.trim();
      }
      return null;
    }
  },
  methods: {
    format_date_time,
    key_for(wf) {
      return wf.id ?? wf.name ?? wf._raw?.workflow_id ?? wf._raw?.name ?? wf._raw?.workflow_name ?? null;
    },
    toggle(wf) {
      const k = this.key_for(wf);
      this.open_key = this.open_key === k ? null : k;
    },
    is_open(wf) {
      const k = this.key_for(wf);
      return k && this.open_key === k;
    },
    state_key(app_id) {
      return `${this.service?.id || this.service?.service_id || 'svc'}:${app_id}`;
    },
    init_app_state(app_id) {
      const key = this.state_key(app_id);
      if (!this.app_data[key]) {
        this.app_data[key] = { loading: false, error: null, response: null, pdf_url: null, customer_image_base_64: null };
      }
      return this.app_data[key];
    },
    app_state(app_id) {
      return this.init_app_state(app_id);
    },
    is_app_open(app_id) {
      return !!this.open_apps[this.state_key(app_id)];
    },
    toggle_app(app_id) {
      const key = this.state_key(app_id);
      this.open_apps[key] = !this.open_apps[key];
      if (this.open_apps[key]) {
        const st = this.init_app_state(app_id);
        if (!st.loading && !st.response && !st.error) {
          this.fetch_service_for_application(app_id);
        }
        this.fetch_overall_severity(app_id);
      }
    },
    risk_key(app) {
      return `${this.state_key(app.id)}:risk`;
    },
    is_risk_open(app) {
      return !!this.open_risk[this.risk_key(app)];
    },
    toggle_risk(app) {
      const k = this.risk_key(app);
      this.open_risk[k] = !this.open_risk[k];
    },
    severity_key(app_id) {
      return this.state_key(app_id) + ':severity';
    },
    overall_severity(app) {
      return this.app_severity[this.severity_key(app.id)] || null;
    },
    has_risk(app) {
      const sev = this.overall_severity(app);
      return !!sev && String(sev).toLowerCase() !== 'unknown';
    },
    get_risk_service_type() {
      const service_type = (this.service?.service_type || '').toLowerCase();
      const is_custom_form = service_type === 'system_generated' || service_type === 'system generated';
      return is_custom_form ? 'custom_form' : 'service';
    },
    async fetch_overall_severity(application_id) {
      const service_id = this.service?.id;
      const app = (this.workflows || []).flatMap(w => w.applications || []).find(a => a.id === application_id);
      const customer_id = app?.customer_id;
      if (!service_id || !application_id || !customer_id) return;
      const key = this.severity_key(application_id);
      if (this.app_severity[key]) return;
      try {
        const TOKEN = localStorage.getItem('token');

        // Determine which endpoint to use based on service type
        // Custom forms use get_overall_severity, other services use get_overall_service_severity
        const service_type = (this.service?.service_type || '').toLowerCase();
        const is_custom_form = service_type === 'system_generated' || service_type === 'system generated';

        const endpoint = is_custom_form
          ? `get_overall_severity/${service_id}/${customer_id}/${application_id}/`
          : `get_overall_service_severity/${service_id}/${customer_id}/${application_id}/`;

        const url = `${API_BASE_URL}/verify_api/workflow_management_api/${endpoint}`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', Authorization: `Token ${TOKEN}` } });
        if (!resp.ok) throw new Error('severity fetch failed');
        const data = await resp.json();
        this.$set ? this.$set(this.app_severity, key, data?.severity ?? 'Unknown') : (this.app_severity[key] = data?.severity ?? 'Unknown');
      } catch (e) {
        this.$set ? this.$set(this.app_severity, key, 'Unknown') : (this.app_severity[key] = 'Unknown');
      }
    },
    async fetch_service_for_application(application_id) {
      const service_id = this.service?.id;
      if (!application_id || !service_id) return;
      const st = this.init_app_state(application_id);
      if (st.loading || st.response || st.error) return;
      st.loading = true;
      st.error = null;
      st.response = null;
      st.pdf_url = null;
      st.customer_image_base_64 = null;
      try {
        const TOKEN = localStorage.getItem('token');
        if (!TOKEN) throw new Error('Missing auth token');
        const url = `${API_BASE_URL}/verify_api/data_hub/applications/${application_id}/services/${service_id}/`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', Authorization: `Token ${TOKEN}` } });
        if (!resp.ok) throw new Error(`Failed to fetch service (${resp.status})`);
        const data = await resp.json();
        const selName = (this.service?.service_name || this.service?.name || '').toLowerCase();
        const { pdf_url, customer_image, response, response_doc, documents } = parseServiceFetchPayload(data, service_id, selName);
        st.pdf_url = pdf_url;
        st.customer_image_base_64 = customer_image;
        // Attach documents and response_doc to response so they're available in the ServiceData component
        st.response = { ...response, response_doc, documents };
      } catch (e) {
        console.error('Workflow app service fetch error', e);
        st.error = e.message || String(e);
      } finally {
        st.loading = false;
      }
    },
    pretty(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },
  },
};
</script>

<style scoped>
.workflow-service-data .service-icon {
  width: 36px;
  height: 36px;
  background: #f8f9fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dee2e6;
}

.workflow-service-data .service-icon i {
  font-size: 1rem;
}

.workflow-body pre {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
}
</style>
